import {appsService} from "../services/appsService";
import {subscriptionManagerService} from "../services/subscriptionManagerService";
import {aggregateAppDataV3, getPaidAppsData} from "../utils/appAggregator";
import Vue from 'vue'
import accountService from "@/modules/account/services/accountService";

let appsLoadingPromise = null;
const offeringsLoadingPromises = {};

const state = {
  apps: [],
  appsLoaded: false,
  billingApp: null,
  billingAccount: null,
  externalApps: [],
  activeSubscriptions: [],
  activeSubscriptionsLoaded: false,
  paidSubscriptions: [],
  appSubscriptions: {}, // { [appCodeName]: boolean }
  offeringsByUid: {}, // { [offeringUid]: Offering | null }
  offeringsByType: {}, // { [type]: Offering[] }
  bundledOfferingsByParent: {}, // { [parentOfferingUid]: Offering[] }
  geoData: null,
};

const helpers = {
  updatePovLayoutOnAppChanges: (isNewLayout) => {
    if (isNewLayout) {
      Vue.prototype.$frontageApi.send('refresh-pov-layout-data')
    }
  }
}

const getters = {
  apps: (state) => state.apps,
  visibleMarketApps: (state) => state.apps.filter(app => app.is_listed),
  appsLoaded: (state) => state.appsLoaded,
  externalApps: (state) => state.externalApps,
  app: (state) => {
    return function (appCodeName) {
      return state.apps.find(a => a.app_code_name == appCodeName)
    }
  },
  externalApp: (state) => {
    return function (appCodeName) {
      return state.externalApps.find(app => app.app_code_name === appCodeName);
    }
  },
  billingApp: (state) => state.billingApp,
  purchasedApps: (state) => {
    return state.apps.filter(app => app.paid_data && !app.paid_data.requires_purchase)
  },
  appUsabilityStatus: (state, getters) => {
    return function (appCodeName, hasAppMarket) {
      let app = getters.app(appCodeName)
      if (!app) return {}
      let status = {
        requires_upgrade: false,
        requires_install: false,
        purchase_state: '',
        requires_purchase: false,
        is_usable: false
      }

      if (app.paid_data) {
        status.purchase_state = app.paid_data.purchase_state
        status.requires_purchase = app.paid_data.requires_purchase
      }
      if (app.requires_upgrade)
        status.requires_upgrade = true
      if (hasAppMarket && app.status != 'installed')
        status.requires_install = true
      if (!status.requires_upgrade && !status.requires_install && !status.requires_purchase)
        status.is_usable = true

      return status
    }
  },
  isSignatureAppAvailable: state => ['esignatures', 'approve'].some(appName => state.apps.find(app => app.app_code_name === appName)),
  isTrackingAppInstalled: state => state.apps.find(app => app.app_code_name === 'conversiontracking')?.status === 'installed',
  isValidBillingAccount: state => {
    return state.billingApp && state.billingAccount && state.billingAccount.code !== 'error' && state.billingAccount.billing_app_account_data.validation_code === 'valid'
  },
  paidSubscriptions: (state) => state.paidSubscriptions,
  hasActiveSubscriptionToApp: (state) => (appCodeName) => {
    return state.appSubscriptions[appCodeName] || false;
  },
  offerings: (state) => (type) => {
    return state.offeringsByType[type] || [];
  },
};

const mutations = {
  setApps(state, filters) {
    state.apps = filters;
    state.appsLoaded = true;
  },
  setBillingApp(state, app) {
    state.billingApp = app;
  },
  setExternalApps(state, apps) {
    state.externalApps = apps;
  },
  setBillingAccount(state, value) {
    state.billingAccount = value;
  },
  setApp(state, value) {
    for (let i = 0; i < state.apps.length; i++) {
      if (state.apps[i].app_code_name === value.app_code_name) {
        Vue.set(state.apps, i, value)
        break
      }
    }
  },
  setActiveSubscriptions(state, activeSubscriptions) {
    state.activeSubscriptions = activeSubscriptions;
    state.activeSubscriptionsLoaded = true;
  },
  setPaidSubscriptions(state, paidSubscriptions) {
    state.paidSubscriptions = paidSubscriptions;
  },
  setAppSubscription(state, {appCodeName, hasSubscription}) {
    Vue.set(state.appSubscriptions, appCodeName, hasSubscription);
  },
  setGeoData(state, geoData) {
    state.geoData = geoData;
  },
  removeApp(state, appCodeName) {
    state.apps = state.apps.filter(app => app.app_code_name !== appCodeName);
  },
  setBundledOfferings(state, {parentOfferingUid, offerings}) {
    Vue.set(state.bundledOfferingsByParent, parentOfferingUid, offerings);
  },
  setOffering(state, {offeringUid, offering}) {
    Vue.set(state.offeringsByUid, offeringUid, offering);
  },
  setOfferings(state, {type, offerings}) {
    Vue.set(state.offeringsByType, type, offerings);
    offerings.forEach((offering) => {
      if (offering?.uid) {
        Vue.set(state.offeringsByUid, offering.uid, offering);
      }
    });
  },
};

const actions = {
  async ensureGeoData({commit, state}) {
    if (state.geoData) return state.geoData;

    const geoData = await accountService.getGeoDataByIP();
    commit('setGeoData', geoData);

    return state.geoData;
  },
  async loadApps({commit, getters, dispatch, rootGetters}, data = {}) {
    const force = Boolean(data.force);

    if (getters.appsLoaded && !force) return;
    if (appsLoadingPromise && !force) return appsLoadingPromise;

    const businessUid = rootGetters?.['accountModule/business']?.uid;

    const thisLoad = (async () => {
      const [apps, offerings, subscriptions, geoData] = await Promise.all([
        appsService.getAppsV3({businessUid, force}),
        dispatch('fetchOfferings', {type: 'app', force}),
        dispatch('fetchActiveSubscriptions', {force}),
        dispatch('ensureGeoData'),
      ]);

      commit('setApps', aggregateAppDataV3(apps, offerings, subscriptions, geoData));
    })();
    appsLoadingPromise = thisLoad;

    try {
      await thisLoad;
    } finally {
      if (appsLoadingPromise === thisLoad) appsLoadingPromise = null;
    }
  },
  async loadBillingAppData({commit}, businessUid) {
    const result = await appsService.getAppsByType({appType: 'billing_app', businessUid: businessUid});
    commit('setBillingApp', result.apps[0]);
  },
  async loadExternalApps({commit, state}) {
    if (state.externalApps.length) return;
    const {apps} = await appsService.getExternalApps();
    commit('setExternalApps', apps);
  },
  async loadBillingAccount({commit}) {
    const billingAccount = await subscriptionManagerService.getBillingAccount();
    commit('setBillingAccount', billingAccount);
  },
  async installApp({rootGetters, commit, getters, dispatch}, params) {
    const result = await appsService.installApp(params);
    if (result?.code === 'error') throw new Error('Failed to install app');

    const existingApp = getters.app(params.app_code_name);
    let app_data = {...existingApp, status: 'installed'};

    if (existingApp.paid_data) {
      app_data = {...app_data, paid_data: existingApp.paid_data};
    }

    helpers.updatePovLayoutOnAppChanges(rootGetters.isMobile);
    commit('setApp', app_data);
    dispatch('invalidateAppMarketCaches');
  },
  async uninstallApp({rootGetters, commit, getters, dispatch}, params) {
    const result = await appsService.uninstallApp(params);
    if (result?.code === 'error') throw new Error('Failed to uninstall app');

    const existingApp = getters.app(params.app_code_name);

    if (existingApp?.uninstall_only) {
      commit('removeApp', params.app_code_name);
    } else {
      let app_data = {...existingApp, status: 'not_installed'};

      if (existingApp.paid_data) {
        app_data = {...app_data, paid_data: {...existingApp.paid_data, purchase_state: 'canceled'}};
      }

      commit('setApp', app_data);
    }

    helpers.updatePovLayoutOnAppChanges(rootGetters.isMobile);
    dispatch('invalidateAppMarketCaches');
    return result;
  },
  async purchaseSubscription({dispatch}, params) {
    let res = await subscriptionManagerService.createSubscription(params);

    if (res.error) {
      throw res.error;
    }

    dispatch('invalidateAppMarketCaches');
  },
  async updateAppPaidData({rootGetters, commit, getters, state, dispatch}, params) {
    const app = getters.app(params.app_code_name);
    dispatch('invalidateAppMarketCaches');
    const subscriptions = await dispatch('fetchActiveSubscriptions', {force: true});
    const paidData = getPaidAppsData([app], getters.offerings('app'), subscriptions, state.geoData);

    if (paidData[params.app_code_name]) {
      helpers.updatePovLayoutOnAppChanges(rootGetters.isMobile);
      commit('setApp', {...app, paid_data: paidData[params.app_code_name]});
    }
  },
  async logUpgradeButtonClicked({commit, state}, selectedApp) {
    await appsService.logUpgradeButtonClicked(selectedApp.app_code_name);
  },
  async fetchActiveSubscriptions({commit, state}, {force = false} = {}) {
    if (!force && state.activeSubscriptionsLoaded) {
      return state.activeSubscriptions;
    }

    try {
      const activeSubscriptions = await subscriptionManagerService.getActiveSubscriptions();
      commit('setActiveSubscriptions', activeSubscriptions);
      return activeSubscriptions;
    } catch (error) {
      console.error('Failed to fetch active subscriptions', error);
      commit('setActiveSubscriptions', []);
      return [];
    }
  },
  invalidateAppMarketCaches({rootGetters}) {
    const businessUid = rootGetters?.['accountModule/business']?.uid;
    if (!businessUid) return;

    appsService.clearAppsCache(businessUid);
    subscriptionManagerService.clearOfferingsCache(businessUid);
  },
  async getPaidSubscriptions({commit, state}) {
    if (state.paidSubscriptions.length) return;
    const paidSubscriptions = await subscriptionManagerService.getPaidSubscriptions();
    commit('setPaidSubscriptions', paidSubscriptions);
  },
  async checkAppSubscription({commit, state}, appCodeName) {
    // Return cached value if already exists
    if (state.appSubscriptions.hasOwnProperty(appCodeName)) {
      return state.appSubscriptions[appCodeName];
    }

    // Fetch from service and cache the result
    try {
      const hasSubscription = await subscriptionManagerService.hasActiveSubscriptionToApp(appCodeName);
      const result = !!hasSubscription; // Ensure boolean
      commit('setAppSubscription', {appCodeName, hasSubscription: result});
      return result;
    } catch (error) {
      console.error('Error checking app subscription:', error);
      commit('setAppSubscription', {appCodeName, hasSubscription: false});
      return false;
    }
  },
  async fetchOfferings({commit, state, rootGetters}, payload = 'app') {
    const {type = 'app', force = false} = typeof payload === 'string' ? {type: payload} : (payload || {});

    if (!force && type in state.offeringsByType) {
      return state.offeringsByType[type];
    }

    if (offeringsLoadingPromises[type]) return offeringsLoadingPromises[type];

    const businessUid = rootGetters?.['accountModule/business']?.uid;

    const thisFetch = (async () => {
      try {
        const offerings = await subscriptionManagerService.getOfferings(type, {businessUid, force});
        commit('setOfferings', {type, offerings});
        return offerings;
      } catch (error) {
        console.error('Failed to fetch offerings for type', type, error);
        commit('setOfferings', {type, offerings: []});
        return [];
      }
    })();
    offeringsLoadingPromises[type] = thisFetch;

    try {
      return await thisFetch;
    } finally {
      if (offeringsLoadingPromises[type] === thisFetch) delete offeringsLoadingPromises[type];
    }
  },
  async fetchOffering({commit, state}, offeringUid) {
    if (!offeringUid) return null;

    if (offeringUid in state.offeringsByUid) {
      return state.offeringsByUid[offeringUid];
    }

    try {
      const offering = await subscriptionManagerService.getOffering(offeringUid);
      commit('setOffering', {offeringUid, offering});
      return offering;
    } catch (error) {
      console.error('Failed to fetch offering', offeringUid, error);
      commit('setOffering', {offeringUid, offering: null});
      return null;
    }
  },
  async fetchBundledOfferings({commit, dispatch, state}, parentOfferingUid) {
    if (!parentOfferingUid) return [];

    if (parentOfferingUid in state.bundledOfferingsByParent) {
      return state.bundledOfferingsByParent[parentOfferingUid];
    }

    try {
      const bundles = await subscriptionManagerService.getBundledOfferings(parentOfferingUid);
      if (!bundles?.length) {
        commit('setBundledOfferings', {parentOfferingUid, offerings: []});
        return [];
      }

      const offerings = await Promise.all(
        bundles.map((bundle) => dispatch('fetchOffering', bundle.bundled_offering_uid))
      );
      const filtered = offerings.filter((offering) => !!offering);

      commit('setBundledOfferings', {parentOfferingUid, offerings: filtered});
      return filtered;
    } catch (error) {
      console.error('Failed to load bundled offerings for', parentOfferingUid, error);
      commit('setBundledOfferings', {parentOfferingUid, offerings: []});
      return [];
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
